import { ChartBarDataset, ChartPieDataset } from "pptxgenjs"
import * as AccountDomain from "../../../../../domain/sfp/account-domain"
import * as ChapterDomain from "../../../../../domain/sfp/chapter-domain"
import * as DemographyDomain from "../../../../../domain/sfp/demography-domain"
import * as ObservationDomain from "../../../../../domain/sfp/observation-domain"
import * as Model from "../../../../../model"
import { ScenarioContext, findAccountValue, getOperationAccounts } from "../../scenario-context"

export function createData(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	receipts: boolean,
	years: number[],
	scenarioContext: ScenarioContext
): Data {
	const data: Data = initData(receipts, years)

	// Montants calculés comme la Synthèse du scénarii de fonctionnement
	getOperationAccounts(scenarioContext, receipts).forEach(laca => {
		if (!laca.chapter) return
		if (receipts && (laca.chapter.number == "011" || laca.chapter.number == "012") ) return
		if (!receipts && (laca.chapter.number == "75") ) return

		const operationsGroup = ChapterDomain.isOrder(laca.chapter)
			? data.orderOperations : data.realOperations

		let row = findRowByChapter(operationsGroup.rows, laca.chapter)

		// si la ligne du chapitre n'existe pas, elle est ajoutée
		if (!row) {
			row = {
				chapter: laca.chapter,
				...createEmptyValuesWithEvolutions(years)
			}
			operationsGroup.rows.push(row)
		}

		years.forEach(year => {
			const value = findAccountValue(scenarioContext, laca, receipts, year) || 0

			row.values[year] += value
			operationsGroup.total.values[year] += value
			data.total.values[year] += value
		})
	})

	cleanEmptyRows(data.orderOperations, years)
	cleanEmptyRows(data.realOperations, years)
	updateEvolutions(data, years)

	// Trie par numéro de chapitre croissant
	data.orderOperations.rows.sort((r1, r2) =>
		ChapterDomain.compareByNumber(r1.chapter, r2.chapter)
	)

	data.realOperations.rows.sort((r1, r2) =>
		ChapterDomain.compareByNumber(r1.chapter, r2.chapter)
	)

	return data
}

    
export interface ProgressData {
    chapter: Model.Chapter;
    accounts: ProgressAccountData[];
}

export interface ProgressAccountData {
    account: any;
    values: {};
}


export function createProgressData(
	localAuthority: Model.LocalAuthority,
	scenario: Model.Scenario,
	ppi: Model.Ppi,
	loanDecompositions: Model.LoanDecomposition[],
	receipts: boolean,
	years: any[],
	referenceYear,
	scenarioContext: ScenarioContext
): ProgressData[] {
	const dataProgress: ProgressData[] = [];
	const nonChaptered: any = { id: -1, label: 'Non chapitré' };

	getOperationAccounts(scenarioContext, receipts).forEach(laca => {
		if (receipts && laca.chapter && (laca.chapter.number == "011" || laca.chapter.number == "012") ) return
		if (!receipts && laca.chapter && (laca.chapter.number == "75") ) return

		// Si le chapitre est null, utiliser "Non chapitré"
		const chapterToAdd = laca.chapter ? laca.chapter : nonChaptered;

		// Vérifier si le chapitre existe déjà dans dataProgress
		const existingChapter = dataProgress.find(
			(progress) => progress.chapter.id === chapterToAdd.id
		);

		// const values: any = [];
		const values:{ [key: string]: number } = {};

		years.forEach(year => {
			const value = findAccountValue(scenarioContext, laca, receipts, year) || 0

			// Vérifier si la valeur existe déjà dans le tableau pour cette année, sinon l'initialiser à 0
			if (!values[year.toString()]) {
				values[year.toString()] = 0;
			}
			// Ajouter la valeur à l'année correspondante
			values[year.toString()] += value;
		})

		const yearReference = years.find(year => year === referenceYear); // Trouver l'année de référence
		const yearPrevious = years.find(year => year === referenceYear-1);	
	
		if (yearReference !== undefined && yearPrevious !== undefined) {
			const valueYearReference = values[yearReference];
			const valueYearPrevious = values[yearPrevious];
			// Si les valeurs existent, calculer la différence
			if (valueYearReference !== undefined && valueYearPrevious !== undefined) {
				const difference = valueYearReference - valueYearPrevious;
				// Ajouter uniquement les lignes ayant un écart de + 5000€ ou - 5000€
				if (difference < 5000 && difference > -5000) return;

				values[`${yearReference}-${yearPrevious}`] = difference;
				// years.push(`${yearReference}-${yearPrevious}`);
			}
		}
	
		if (existingChapter) {
			// Si le chapitre existe, vérifier si le compte existe déjà dans accounts
			const existingAccount = existingChapter.accounts.find(
				(acc) => acc.account.id === laca.account.id
			);
	
			if (existingAccount) {
				// Si le compte existe déjà, ajouter les nouvelles valeurs
				for (const year in values) {
					if (values.hasOwnProperty(year)) { // Vérifiez que c'est une propriété de l'objet
						const value = values[year]; // Obtenez la valeur correspondante
						// Ajoutez chaque valeur pour l'année correspondante
						if (!existingAccount.values[year]) {
							existingAccount.values[year] = 0; // Initialiser la valeur si elle n'existe pas
						}
						existingAccount.values[year] += value; // Mettre à jour la valeur
					}
				}			
			} else {
				// Si le compte n'existe pas, l'ajouter avec ses valeurs
				existingChapter.accounts.push({
					account: laca.account,
					values: { ...values }
				});
			}
		} else {
			// Si le chapitre n'existe pas, créer une nouvelle entrée avec le compte et ses valeurs
			dataProgress.push({
				chapter: chapterToAdd,
				accounts: [{
					account: laca.account,
					values: { ...values } // Ajouter les valeurs directement lors de la création du chapitre
				}]
			});
		}
	});

	// on tri les comptes par numéro
	dataProgress.forEach(progress => {
		progress.accounts.sort((a, b) => {
			// Comparer d'abord par longueur, puis par valeur numérique si les longueurs sont égales
			if (a.account.number.length !== b.account.number.length) {
			  return a.account.number.length - b.account.number.length;
			} else {
			  return a.account.number.localeCompare(b.account.number);
			}
		  });
	});

	return dataProgress;
}




export function createGlobalChaptersData(
	data: Data,
	years: number[]
): ChartBarDataset[] {
	return data.realOperations.rows.map<ChartBarDataset>(r => {
		return {
			labels: years.map(y => y.toString()),
			name: r.chapter ? r.chapter.label : "Non chapitré",
			values: years.map(y => r.values[y])
		}
	})
}

export function createGlobalPerInhabitantData(
	globalChaptersData: ChartBarDataset[],
	demographies: Model.Demography[],
	years: number[]
): ChartBarDataset[] {
	// Copie des data en profondeur
	const data = JSON.parse(JSON.stringify(globalChaptersData)) as ChartBarDataset[]

	data.forEach(dataset => {
		years.forEach((year, yearIndex) => {
			const demography = DemographyDomain.findByYear(demographies, year)
			dataset.values[yearIndex] = demography && demography.population
				? dataset.values[yearIndex] / demography.population
				: 0
		})
	})

	return data
}

export function createPerChapterData(
	name: string,
	chapter: Model.Chapter,
	observations: Model.Observation[],
	lacas: Model.LocalAuthorityChapterAccount[],
	year: number,
	receipts: boolean
): ChartPieDataset[] {
	const data: ChartPieDataset[] = [{ labels: [], name, values: [] }]
	const budgetObservations = getBudgetObservations(receipts, observations)
	const chapterObservations = ObservationDomain.filterObservationsByChapter(
		budgetObservations,
		chapter,
		lacas
	)
	const accounts = AccountDomain.distinctAccounts(chapterObservations)

	accounts
	.sort((account1, account2) => AccountDomain.compareByNumber(account1, account2))
	.forEach((account, accountIndex) => {
		data[0].labels.push(AccountDomain.titlize(account))
		data[0].values.push(0)

		for (const observation of observations) {
			if (!AccountDomain.equal(account, observation.account)) continue
			if (year !== observation.year) continue
			data[0].values[accountIndex] += observation.amount
		}
	})

	return data
}

export function createPerChapterPerInhabitantData(
	name: string,
	chapter: Model.Chapter,
	observations: Model.Observation[],
	demographies: Model.Demography[],
	lacas: Model.LocalAuthorityChapterAccount[],
	year: number,
	receipts: boolean
): ChartPieDataset[] {
	const perChapterData = createPerChapterData(
		name,
		chapter,
		observations,
		lacas,
		year,
		receipts
	)
	const demography = DemographyDomain.findByYear(demographies, year)
	perChapterData[0].values.forEach((value, valueIndex, values) => {
		values[valueIndex] = demography && demography.population
			? value / demography.population
			: 0
	})

	return perChapterData
}

export function findEvolution(
	evolutions: Evolution[],
	prevYear?: number,
	nextYear?: number
): Evolution {
	if (undefined !== prevYear)
		return evolutions.find(e => e.prevYear === prevYear) || null

	if (undefined !== nextYear)
		return evolutions.find(e => e.nextYear === nextYear) || null

	return null
}

export interface Data {
	orderOperations: DataGroup
	realOperations: DataGroup
	total: Total
}

export interface DataGroup {
	label: string
	rows: DataRow[],
	total: ValuesWithEvolutions
}

export interface DataRow extends ValuesWithEvolutions {
	chapter: Model.Chapter
}

export interface Evolution {
	nextYear: number
	prevYear: number
	value: number
}

export interface Total extends ValuesWithEvolutions {
	label: string
}

export interface ValuesWithEvolutions {
	values: YearsValues
	evolutions: Evolution[]
}

export interface YearsValues {
	[key: number]: number
}

function calculateEvolution(value: number, prevValue: number): number | null {
	if (0 === prevValue) return null
	return value / prevValue - 1
}

function cleanEmptyRows(group: DataGroup, years: number[]): void {
	for (let rowIndex = group.rows.length - 1; rowIndex >= 0; rowIndex--) {
		const row = group.rows[rowIndex]

		if (years.every(year => 0 === row.values[year]))
			group.rows.splice(rowIndex, 1)
	}
}

function createEmptyValuesWithEvolutions(years: number[]): ValuesWithEvolutions {
	const values = createEmptyYearsValues(years)
	const evolutions = createEvolutions(values, years)
	return { values, evolutions }
}

function createEmptyYearsValues(years: number[]): YearsValues {
	return years.reduce<YearsValues>((result, year) => {
		result[year] = 0
		return result
	}, {})
}

function createEvolutions(values: YearsValues, years: number[]): Evolution[] {
	return years.reduce<Evolution[]>((evolutions, year, i) => {
		if (0 === i) return evolutions
		const prevYear = years[i - 1]
		evolutions.push({
			prevYear,
			value: calculateEvolution(values[year], values[prevYear]),
			nextYear: year,
		})
		return evolutions
	}, [])
}

function getBudgetObservations(
	receipts: boolean,
	observations: Model.Observation[]
) {
	return receipts
		? ObservationDomain.getOperationReceiptsObservations(observations)
		: ObservationDomain.getOperationExpenseObservations(observations);
}

function initData(receipts: boolean, years: number[]): Data {
	return {
		orderOperations: {
			label: "Opérations d'ordre",
			rows: [],
			total: createEmptyValuesWithEvolutions(years)
		},
		realOperations: {
			label: `${receipts ? "Recettes" : "Dépenses"} réelles de fonctionnement`,
			rows: [],
			total: createEmptyValuesWithEvolutions(years)
		},
		total: {
			label: `Total ${receipts ? "recettes" : "dépenses"} de fonctionnement`,
			...createEmptyValuesWithEvolutions(years)
		}
	}
}

function findRowByChapter(rows: DataRow[], chapter: Model.Chapter): DataRow {
	return rows.find(row => ChapterDomain.equal(row.chapter, chapter))
}

function updateEvolutions(data: Data, years: number[]): void {
	data.orderOperations.rows.forEach(row =>
		row.evolutions = createEvolutions(row.values, years))

	data.orderOperations.total.evolutions = createEvolutions(
		data.orderOperations.total.values, years)

	data.realOperations.rows.forEach(row =>
		row.evolutions = createEvolutions(row.values, years))

	data.realOperations.total.evolutions = createEvolutions(
		data.realOperations.total.values, years)

	data.total.evolutions = createEvolutions(data.total.values, years)
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/operation-generator/utils.ts